import { useState } from 'react';
import { CheckIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Badge } from '@tailwindComponents/Badge';
import {
  SkuActionsMenu,
  SkuMenuOption,
} from '@views/SKUMaster/components/SkuActionsMenu';
import {
  Customer,
  CustomerReadiness,
  statusBadgeIntentOf,
  statusLabelOf,
} from './customerTypes';
import { PartnerChips } from './PartnerChips';

export interface CustomerTableRowProps {
  customer: Customer;
  menuOptions: SkuMenuOption[];
  isCopied: boolean;
  onCopy: (customer: Customer) => void;
}

function ReadinessCell({ readiness }: { readiness: CustomerReadiness | null }) {
  if (!readiness) return <span className='text-sm text-gray-400'>—</span>;
  if (readiness.ready) {
    return (
      <Badge
        intent='success'
        label='Despatch-ready'
        icon={<CheckIcon className='h-3.5 w-3.5' />}
      />
    );
  }
  return (
    <Badge
      intent='warning'
      label={`${readiness.gaps} gap${readiness.gaps === 1 ? '' : 's'}`}
    />
  );
}

function ContactDetail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className='text-xs font-medium text-gray-500'>{label}</p>
      <p className='text-sm text-gray-900'>{value || '—'}</p>
    </div>
  );
}

const nameSubtext = (customer: Customer): string => {
  const { hierarchy, recordType } = customer;
  if (hierarchy.bucket === 'independent') return 'Independent';
  if (hierarchy.bucket === 'unassigned') return 'Unassigned';

  const parts: string[] = [];
  if (recordType === 'Site') {
    if (hierarchy.parentName) parts.push(hierarchy.parentName);
    if (hierarchy.isShipToSite) parts.push('Ship-to site');
    if (hierarchy.isDemandNode) parts.push('Demand node');
  } else {
    const path = [hierarchy.groupName, hierarchy.subNodeName]
      .filter((part): part is string => !!part)
      .join(' › ');
    if (path) parts.push(path);
    if (hierarchy.banner) parts.push(`Banner: ${hierarchy.banner}`);
  }
  return parts.join(' · ');
};

export function CustomerTableRow({
  customer,
  menuOptions,
  isCopied,
  onCopy,
}: CustomerTableRowProps) {
  const subtext = nameSubtext(customer);
  const [isShowingContactDetails, setIsShowingContactDetails] = useState(false);

  return (
    <>
      <tr className='align-middle hover:bg-gray-50'>
        <td className='w-10 px-2'>
          <button
            type='button'
            aria-expanded={isShowingContactDetails}
            aria-label={`${
              isShowingContactDetails ? 'Hide' : 'Show'
            } contact details for ${customer.name}`}
            className='rounded p-1 transition-colors hover:bg-gray-200'
            onClick={() =>
              setIsShowingContactDetails((isShowing) => !isShowing)
            }
          >
            <ChevronRightIcon
              className={`h-4 w-4 text-gray-500 transition-transform ${
                isShowingContactDetails ? 'rotate-90' : ''
              }`}
            />
          </button>
        </td>
        <td className='px-4 py-4'>
          <p className='truncate text-sm font-semibold text-gray-900'>
            {customer.name}
          </p>
          {subtext && (
            <p className='truncate text-xs text-gray-500'>{subtext}</p>
          )}
        </td>
        <td className='px-4 py-4'>
          <div className='flex items-center gap-1.5 text-sm text-gray-600'>
            <span className='truncate'>{customer.customerId}</span>
            <button
              type='button'
              aria-label={`Copy customer ID for ${customer.name}`}
              className='shrink-0'
              onClick={(e) => {
                e.stopPropagation();
                onCopy(customer);
              }}
            >
              {isCopied ? (
                <CheckIcon className='h-4 w-4 text-emerald-600' />
              ) : (
                <ClipboardDocumentIcon className='h-4 w-4 text-gray-400 hover:text-primary' />
              )}
            </button>
          </div>
        </td>
        <td className='px-4 py-4'>
          <Badge
            intent={customer.recordType === 'Site' ? 'neutral' : 'info'}
            label={customer.typeLabel}
          />
        </td>
        <td className='px-4 py-4'>
          {customer.market ? (
            <Badge intent='neutral' label={customer.market} />
          ) : (
            <span className='text-sm text-gray-400'>—</span>
          )}
        </td>
        <td className='px-4 py-4'>
          {customer.partners.payerRef || customer.partners.billToRef ? (
            <PartnerChips partners={customer.partners} />
          ) : (
            <span className='text-sm text-gray-400'>—</span>
          )}
        </td>
        <td className='px-4 py-4'>
          <ReadinessCell readiness={customer.readiness} />
        </td>
        <td className='px-4 py-4'>
          {customer.isArchived ? (
            <Badge intent='neutral' label='Archived' />
          ) : (
            <Badge
              intent={statusBadgeIntentOf(customer.status)}
              label={statusLabelOf(customer.status)}
            />
          )}
        </td>
        <td className='px-4 py-4'>
          <div className='flex items-center justify-end'>
            <SkuActionsMenu id={customer.customerId} options={menuOptions} />
          </div>
        </td>
      </tr>
      {isShowingContactDetails && (
        <tr className='bg-gray-50/70'>
          <td className='w-10 px-2' />
          <td colSpan={8} className='px-4 py-4'>
            <div className='grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-5'>
              <ContactDetail label='Address' value={customer.address} />
              <ContactDetail label='Postcode' value={customer.postcode} />
              <ContactDetail label='Country' value={customer.country} />
              <ContactDetail label='Phone' value={customer.contactNumber} />
              <ContactDetail label='Email' value={customer.contactEmail} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
